<template>
  <div>
    <b-card class="p-0 filter-card rounded-lg">
      <b-row>
        <b-col cols="12">
          <h3 class="ml-1 mb-2">
            {{ $t('accounting_transfers_filters') || 'Filters' }}
          </h3>
        </b-col>
        <b-col cols="3">
          <v-select
            v-model="filter.status"
            :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
            :options="statusOptions"
            class="w-100 mb-0"
            :reduce="val => val.value"
            :placeholder="$t('accounting_transfers_status') || 'Status'"
            :label="$i18n.locale == 'en' ? 'label' : 'label_ar'"
            clearable
          >
            <template v-slot:option="option">
              {{ $i18n.locale == "ar" ? option.label_ar : option.label }}
            </template>
            <template #selected-option="{ label, label_ar }">
              <div style="display: flex; align-items: baseline">
                <strong v-if="$i18n.locale == 'ar'">{{ label_ar }} </strong>
                <strong v-else>{{ label }} </strong>
              </div>
            </template>
            <template #no-options>
              {{ $t("noMatching") }}
            </template>
          </v-select>
        </b-col>
        <b-col cols="3">
          <v-select
            v-model="filter.from_source_id"
            :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
            :options="accounts"
            class="w-100 mb-0"
            :reduce="val => val.id"
            :get-option-label="option => `${option.code} - ${option.name}`"
            :placeholder="$t('accounting_transfers_from_account') || 'From Account'"
            clearable
            @input="loadAccounts"
          />
        </b-col>
        <b-col cols="3">
          <v-select
            v-model="filter.to_source_id"
            :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
            :options="accounts"
            class="w-100 mb-0"
            :reduce="val => val.id"
            :get-option-label="option => `${option.code} - ${option.name}`"
            :placeholder="$t('accounting_transfers_to_account') || 'To Account'"
            clearable
            @input="loadAccounts"
          />
        </b-col>
        <b-col cols="3">
          <v-select
            v-model="filter.sort_by"
            :dir="$store.state.appConfig.layout.isRTL ? 'rtl' : 'ltr'"
            :options="sortOptions"
            class="w-100 mb-0"
            :reduce="val => val.value"
            :placeholder="$t('accounting_transfers_sort_by') || 'Sort By'"
            :label="$i18n.locale == 'en' ? 'label' : 'label_ar'"
          >
            <template v-slot:option="option">
              {{ $i18n.locale == "ar" ? option.label_ar : option.label }}
            </template>
            <template #selected-option="{ label, label_ar }">
              <div style="display: flex; align-items: baseline">
                <strong v-if="$i18n.locale == 'ar'">{{ label_ar }} </strong>
                <strong v-else>{{ label }} </strong>
              </div>
            </template>
          </v-select>
        </b-col>
      </b-row>
    </b-card>
    <b-card class="w-100 d-flex rounded-lg">
      <b-row>
        <b-col
          md="12"
          class="pb-2 w-100 d-flex justify-content-between"
        >
          <b-button
            v-if="hasPermission('create transfers')"
            class="btn rounded-pill ml-auto"
            variant="primary"
            @click="gotoCreateTransfer()"
          >
            {{ $t('accounting_transfers_add_transfer') || 'Add Transfer' }}
          </b-button>
        </b-col>
      </b-row>

      <div
        v-if="load"
        class="text-center"
        style="width: 100%;"
      >
        <b-spinner
          variant="primary"
          label="Spinning"
        />
      </div>

      <b-table
        v-else
        striped
        :items="items.data || items"
        :fields="fields"
        responsive
      >
        <template #cell(transfer_number)="data">
          <b-link :to="{ name: 'accounting-transfers-view', params: { id: data.item.id } }">
            {{ data.item.transfer_number }}
          </b-link>
        </template>
        <template #cell(from_source)="data">
          <div v-if="data.item.from_source">
            <div><strong>{{ data.item.from_source.code }}</strong></div>
            <div class="text-muted small">
              {{ data.item.from_source.name }}
            </div>
          </div>
          <span v-else>-</span>
        </template>
        <template #cell(to_source)="data">
          <div v-if="data.item.to_source">
            <div><strong>{{ data.item.to_source.code }}</strong></div>
            <div class="text-muted small">
              {{ data.item.to_source.name }}
            </div>
          </div>
          <span v-else>-</span>
        </template>
        <template #cell(amount)="data">
          {{ formatCurrency(data.item.amount) }}
        </template>
        <template #cell(status)="data">
          <b-badge :variant="getStatusVariant((data.item.status && data.item.status.value) || '')">
            {{ $i18n.locale === 'ar' ? (data.item.status && data.item.status.label_ar) || '' : (data.item.status && data.item.status.label) || '' }}
          </b-badge>
        </template>
        <template #cell(created_at)="data">
          {{ formatDate(data.item.created_at) }}
        </template>
        <template #cell(actions)="data">
          <div
            v-if="data.item"
            class="d-flex align-items-center"
          >
            <b-link
              v-b-tooltip.hover.top="$t('accounting_transfers_view') || 'View Transfer'"
              class="ml-1"
              :to="{ name: 'accounting-transfers-view', params: { id: data.item.id} }"
            >
              <feather-icon
                icon="EyeIcon"
                size="18"
              />
            </b-link>
            <b-link
              v-if="(data.item.status && data.item.status.value === 'pending') && hasPermission('approve transfers')"
              v-b-tooltip.hover.top="$t('accounting_transfers_approve') || 'Approve Transfer'"
              class="ml-1 text-success"
              @click="approveTransfer(data.item.id)"
            >
              <feather-icon
                icon="CheckIcon"
                size="18"
              />
            </b-link>
            <b-link
              v-if="(data.item.status && data.item.status.value === 'pending') && hasPermission('cancel transfers')"
              v-b-tooltip.hover.top="$t('accounting_transfers_cancel') || 'Cancel Transfer'"
              class="ml-1 text-danger"
              @click="cancelTransfer(data.item.id)"
            >
              <feather-icon
                icon="XIcon"
                size="18"
              />
            </b-link>
          </div>
        </template>
      </b-table>
      <b-pagination
        v-if="items.meta"
        v-model="currentPage"
        align="center"
        :total-rows="items.meta.total"
        :per-page="items.meta.per_page"
        @change="onPageChange"
      />
    </b-card>
  </div>
</template>

<script>
import {
  BTable,
  BButton,
  BCard,
  BRow,
  BCol,
  BBadge,
  BLink,
  VBTooltip,
  BSpinner,
  BPagination,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { mapGetters } from 'vuex'
import vSelect from 'vue-select'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import * as accountsAPI from '@/api/accounting/accounts'

export default {
  components: {
    BTable,
    BButton,
    BCard,
    BRow,
    BCol,
    BBadge,
    BLink,
    BSpinner,
    BPagination,
    vSelect,
    FeatherIcon,
    ToastificationContent,
  },
  computed: {
    ...mapGetters({
      items: 'accounting/transfers/items',
      load: 'accounting/transfers/load',
    }),
  },
  directives: {
    Ripple,
    'b-tooltip': VBTooltip,
  },
  watch: {
    'filter.status': function () {
      this.loadTransfers()
    },
    'filter.from_source_id': function () {
      this.loadTransfers()
    },
    'filter.to_source_id': function () {
      this.loadTransfers()
    },
  },
  data() {
    return {
      currentPage: 1,
      accounts: [],
      filter: {
        status: null,
        from_source_type: 'account',
        from_source_id: null,
        to_source_type: 'account',
        to_source_id: null,
        sort_by: 'created_at',
        sort_order: 'desc',
        per_page: 15,
        page: 1,
      },
      statusOptions: [
        { value: 'pending', label: 'Pending', label_ar: 'قيد الانتظار' },
        { value: 'completed', label: 'Completed', label_ar: 'مكتمل' },
        { value: 'cancelled', label: 'Cancelled', label_ar: 'ملغي' },
      ],
      sortOptions: [
        { value: 'created_at', label: 'Created At', label_ar: 'تاريخ الإنشاء' },
        { value: 'amount', label: 'Amount', label_ar: 'المبلغ' },
      ],
      fields: [],
    }
  },
  mounted() {
    this.init()
  },
  methods: {
    hasPermission(permission) {
      const userPermissions = this.$store.getters['users/user_permissions']
      if (Array.isArray(userPermissions)) {
        return userPermissions.includes(permission)
      }
      return Object.values(userPermissions || {}).includes(permission)
    },
    gotoCreateTransfer() {
      this.$router.push({ name: 'accounting-transfers-create' })
    },
    onPageChange(page) {
      this.currentPage = page
      this.filter.page = page
      this.loadTransfers()
    },
    loadTransfers() {
      const params = { ...this.filter }
      if (!params.status) delete params.status
      if (!params.from_source_id) delete params.from_source_id
      if (!params.to_source_id) delete params.to_source_id
      this.$store.dispatch('accounting/transfers/list', params)
    },
    async loadAccounts() {
      try {
        const response = await accountsAPI.getAccounts({ is_active: true, per_page: 1000 })
        this.accounts = response.data || []
      } catch (error) {
        console.error('Error loading accounts:', error)
      }
    },
    formatCurrency(amount, currency = 'EGP') {
      return new Intl.NumberFormat('ar-EG', {
        style: 'currency',
        currency,
        minimumFractionDigits: 2,
      }).format(amount)
    },
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString(this.$i18n.locale === 'ar' ? 'ar-EG' : 'en-US')
    },
    getStatusVariant(status) {
      const variants = {
        pending: 'warning',
        completed: 'success',
        cancelled: 'danger',
      }
      return variants[status] || 'secondary'
    },
    async approveTransfer(id) {
      try {
        await this.$store.dispatch('accounting/transfers/approve', id)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('accounting_transfers_messages.approved_success') || 'Transfer approved successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        this.loadTransfers()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('accounting_transfers_messages.error') || 'An error occurred',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },
    async cancelTransfer(id) {
      try {
        await this.$store.dispatch('accounting/transfers/cancel', id)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('accounting_transfers_messages.cancelled_success') || 'Transfer cancelled successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
        this.loadTransfers()
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('accounting_transfers_messages.error') || 'An error occurred',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
      }
    },
    init() {
      this.fields = [
        { key: 'transfer_number', label: this.$t('accounting_transfers_number') || 'Transfer Number', sortable: true },
        { key: 'from_source', label: this.$t('accounting_transfers_from_account') || 'From Account' },
        { key: 'to_source', label: this.$t('accounting_transfers_to_account') || 'To Account' },
        { key: 'amount', label: this.$t('accounting_transfers_amount') || 'Amount', sortable: true },
        { key: 'status', label: this.$t('accounting_transfers_status') || 'Status' },
        { key: 'created_at', label: this.$t('accounting_transfers_created_at') || 'Created At', sortable: true },
        { key: 'actions', label: this.$t('accounting_accounts_actions') || 'Actions' },
      ]
      this.loadAccounts()
      this.loadTransfers()
    },
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
